import React, { useEffect, useState, useRef, lazy, Component } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Library, ShieldCheck, Eye, Activity, AlertTriangle, HeartPulse, Brain, ClipboardList, Stethoscope, FileText } from 'lucide-react';
interface Preview {
  src: string;
  label: string;
  caption: string;
  icon: ComponentType<any>;
}
const PREVIEWS: Preview[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/6FRN1JRedjQvZLfBMCo3PS/IMG_1887.jpg',
  label: 'Estructura del sistema',
  caption: 'Cómo está organizado el sistema clínico',
  icon: Library
}, {
  src: 'https://cdn.magicpatterns.com/uploads/9TNea88Mz8FeMiC4Tgcmta/IMG_1880.jpg',
  label: 'Errores a evitar',
  caption: 'Errores comunes que no podés cometer',
  icon: ShieldCheck
}, {
  src: 'https://cdn.magicpatterns.com/uploads/ngkErqWzQVcyDmjnHo9f9A/IMG_1881.jpg',
  label: 'Cómo actuar frente al paciente',
  caption: 'Qué hacer en los primeros 30 segundos',
  icon: Eye
}, {
  src: 'https://cdn.magicpatterns.com/uploads/9g8CS82RFKhCsTN29ta1XA/IMG_1882.jpg',
  label: 'Cómo interpretar signos',
  caption: 'Signos vitales y qué significa cada cambio',
  icon: HeartPulse
}, {
  src: 'https://cdn.magicpatterns.com/uploads/pqyL3D4jkzGHYWyaeuWodc/IMG_1888.jpg',
  label: 'Red flags importantes',
  caption: 'Señales de alarma que no podés ignorar',
  icon: AlertTriangle
}, {
  src: 'https://cdn.magicpatterns.com/uploads/pixAJE29rPHvRq4hnEtFdc/IMG_1883.jpg',
  label: 'Qué preguntar primero',
  caption: 'Valoración sistemática del dolor',
  icon: Activity
}, {
  src: 'https://cdn.magicpatterns.com/uploads/gV4ZY2US6bXRsPtCq2xJfB/IMG_1884.jpg',
  label: 'Escalas clínicas',
  caption: 'Cómo aplicar la Escala de Glasgow',
  icon: Brain
}, {
  src: 'https://cdn.magicpatterns.com/uploads/krKbrPC1y2aqNQd7DM5hds/IMG_1889.jpg',
  label: 'Protocolos paso a paso',
  caption: 'Cómo razonar causas de disnea',
  icon: ClipboardList
}, {
  src: 'https://cdn.magicpatterns.com/uploads/9fmhvpGjubAiD5kzMmtzdY/IMG_1890.jpg',
  label: 'Qué priorizar primero',
  caption: 'Cómo actuar frente a una caída',
  icon: Stethoscope
}, {
  src: 'https://cdn.magicpatterns.com/uploads/ixuCTDdq5LB18AffC5GZcU/IMG_1891.jpg',
  label: 'Razonamiento clínico',
  caption: 'Cómo pensar un caso clínico real',
  icon: FileText
}];
export function ContentPreview() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  // Drag state — refs only (no re-renders during drag)
  const isDown = useRef(false);
  const startX = useRef(0);
  const startScroll = useRef(0);
  const cachedOffsetLeft = useRef(0);
  const cachedCardWidth = useRef(0);
  const rafId = useRef<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Batch layout reads inside requestAnimationFrame to avoid forced reflows
  const updateScrollState = () => {
    if (rafId.current !== null) return;
    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;
      const el = scrollRef.current;
      if (!el) return;
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = el;
      setCanScrollLeft(scrollLeft > 8);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 8);
    });
  };
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Initial state — measure once
    const card = el.querySelector('[data-preview-card]') as HTMLElement | null;
    cachedCardWidth.current = card?.clientWidth ?? 280;
    cachedOffsetLeft.current = el.getBoundingClientRect().left + window.scrollX;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, {
      passive: true
    });
    const onResize = () => {
      const c = el.querySelector('[data-preview-card]') as HTMLElement | null;
      cachedCardWidth.current = c?.clientWidth ?? 280;
      cachedOffsetLeft.current = el.getBoundingClientRect().left + window.scrollX;
      updateScrollState();
    };
    window.addEventListener('resize', onResize, {
      passive: true
    });
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', onResize);
      if (rafId.current !== null) cancelAnimationFrame(rafId.current);
    };
  }, []);
  const scrollBy = (dir: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardW = cachedCardWidth.current || 280;
    el.scrollBy({
      left: dir * (cardW + 20),
      behavior: 'smooth'
    });
  };
  // Drag handlers — use cached offset instead of reading el.offsetLeft on every move
  const onMouseDown = (e: React.MouseEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    isDown.current = true;
    startX.current = e.pageX - cachedOffsetLeft.current;
    startScroll.current = el.scrollLeft;
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDown.current) return;
    const el = scrollRef.current;
    if (!el) return;
    e.preventDefault();
    const x = e.pageX - cachedOffsetLeft.current;
    const walk = (x - startX.current) * 1.2;
    if (Math.abs(walk) > 5 && !isDragging) setIsDragging(true);
    el.scrollLeft = startScroll.current - walk;
  };
  const onMouseUp = () => {
    isDown.current = false;
    if (isDragging) setTimeout(() => setIsDragging(false), 50);
  };
  return <section className="relative py-12 md:py-20 lg:py-24 bg-white overflow-hidden" data-id="element-46">
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[28rem] h-[28rem] bg-clinical-soft rounded-full blur-[140px] opacity-40 pointer-events-none" data-id="element-47"></div>
      <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[24rem] h-[24rem] bg-clinical-softer rounded-full blur-[140px] opacity-60 pointer-events-none" data-id="element-48"></div>

      <div className="relative max-w-[1200px] mx-auto" data-id="element-49">
        <div className="text-center max-w-2xl mx-auto mb-8 md:mb-12 px-4 sm:px-6 lg:px-8" data-id="element-50">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-[10px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-clinical-mid mb-4" data-id="element-51">
            El sistema por dentro
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-2xl sm:text-4xl md:text-5xl font-black text-clinical-deep mb-4 leading-[1.05] uppercase" data-id="element-52">
            Así se ve el sistema{' '}
            <span className="accent-italic normal-case" data-id="element-53">por dentro</span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-sm md:text-lg text-clinical-ink leading-relaxed" data-id="element-54">
            Páginas reales del Mega Pack para que entiendas{' '}
            <span className="accent-italic" data-id="element-55">
              qué hacer, qué priorizar y cómo actuar
            </span>{' '}
            frente al paciente.
          </motion.p>
        </div>

        <div className="hidden md:flex absolute top-1/2 right-6 lg:right-10 -translate-y-1/2 z-20 gap-2" data-id="element-56">
          <button type="button" onClick={() => scrollBy(-1)} disabled={!canScrollLeft} aria-label="Anterior" className={`w-11 h-11 rounded-full bg-white border border-clinical-line shadow-[0_8px_24px_rgba(11,42,74,0.12)] flex items-center justify-center transition-all ${canScrollLeft ? 'text-clinical-deep hover:bg-clinical-deep hover:text-white hover:border-clinical-deep' : 'text-clinical-ink/30 cursor-not-allowed opacity-60'}`} data-id="element-57">
            <ChevronLeft className="w-5 h-5" strokeWidth={2.5} data-id="element-58" />
          </button>
          <button type="button" onClick={() => scrollBy(1)} disabled={!canScrollRight} aria-label="Siguiente" className={`w-11 h-11 rounded-full bg-white border border-clinical-line shadow-[0_8px_24px_rgba(11,42,74,0.12)] flex items-center justify-center transition-all ${canScrollRight ? 'text-clinical-deep hover:bg-clinical-deep hover:text-white hover:border-clinical-deep' : 'text-clinical-ink/30 cursor-not-allowed opacity-60'}`} data-id="element-59">
            <ChevronRight className="w-5 h-5" strokeWidth={2.5} data-id="element-60" />
          </button>
        </div>

        <div className="relative" data-id="element-61">
          <div className="hidden md:block absolute top-0 left-0 bottom-4 w-12 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" data-id="element-62"></div>
          <div className="hidden md:block absolute top-0 right-0 bottom-4 w-12 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" data-id="element-63"></div>

          <div ref={scrollRef} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp} className={`flex gap-4 md:gap-5 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 px-4 sm:px-6 lg:px-10 ${isDragging ? 'cursor-grabbing select-none' : 'md:cursor-grab'}`} style={{
          scrollPaddingLeft: '1rem',
          scrollPaddingRight: '1rem'
        }} data-id="element-64">
            {PREVIEWS.map((preview, idx) => <PreviewCard key={idx} preview={preview} idx={idx} data-id="element-65" />)}
            <div className="shrink-0 w-2 md:w-6" aria-hidden="true" data-id="element-66"></div>
          </div>
        </div>

        <div className="md:hidden flex items-center justify-center gap-1.5 mt-3 text-[11px] font-medium text-clinical-ink/50 px-4" data-id="element-67">
          <ChevronLeft className="w-3 h-3" strokeWidth={2.5} data-id="element-68" />
          Deslizá para ver más
          <ChevronRight className="w-3 h-3" strokeWidth={2.5} data-id="element-69" />
        </div>
      </div>
    </section>;
}
function PreviewCard({
  preview,
  idx
}: {
  preview: Preview;
  idx: number;
}) {
  const Icon = preview.icon;
  return <motion.div data-preview-card initial={{
    opacity: 0,
    y: 24
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-40px'
  }} transition={{
    delay: Math.min(idx * 0.06, 0.4),
    duration: 0.5,
    ease: 'easeOut'
  }} className="group shrink-0 snap-center w-[230px] sm:w-[260px] md:w-[300px]" data-id="element-70">
      <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-white border border-clinical-line shadow-[0_12px_32px_rgba(11,42,74,0.08)] group-hover:shadow-[0_20px_48px_rgba(11,42,74,0.16)] group-hover:-translate-y-1 group-hover:border-clinical-mid/40 transition-all duration-300 will-change-transform" data-id="element-71">
        <div className="absolute inset-0 bg-gradient-to-br from-clinical-soft/0 to-clinical-soft/0 group-hover:from-clinical-soft/40 group-hover:to-transparent transition-opacity duration-500 pointer-events-none z-10" data-id="element-72"></div>

        <img src={preview.src} alt={preview.caption} width={600} height={800} draggable={false} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none" data-id="element-73" />

        <div className="absolute inset-0 ring-1 ring-inset ring-clinical-deep/5 rounded-2xl pointer-events-none" data-id="element-74"></div>
      </div>

      <div className="mt-4 px-1" data-id="element-75">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-clinical-base border border-clinical-line text-[10px] font-bold tracking-[0.12em] uppercase text-clinical-mid mb-2" data-id="element-76">
          <Icon className="w-3 h-3" strokeWidth={2.5} data-id="element-77" />
          {preview.label}
        </div>
        <p className="text-sm md:text-[15px] font-semibold text-clinical-deep leading-snug" data-id="element-78">
          {preview.caption}
        </p>
      </div>
    </motion.div>;
}